export interface Certification {
  profile_id: string;
  profile_type: "business" | "personal";
  status: "approved" | "pending" | "rejected" | "revoked" | "cancelled" | "review_requested";
  submitted_at: string;
  updated_at: string;
  verified_domains: CertificationDomain[];
  business: CertificationProfileBusiness | null;
  personal: CertificationProfilePersonal | null;
  pending_domains_count: number;
}

export interface CertificationProfile {
  profile_type: "business" | "personal";
  business?: CertificationProfileBusiness;
  personal?: CertificationProfilePersonal;
  verified_domains: CertificationDomain[];
}

export interface CertificationProfileCreate extends CertificationProfile {
  profile_id?: string;
  uploaded_files?: File[];
  old_documents?: CertificationDocument[];
}

export interface CertificationProfileDetail extends CertificationProfile {
  profile_id?: string;
  status: "approved" | "pending" | "rejected" | "revoked" | "cancelled" | "review_requested";
  submitted_at: string;
  approved_at: string;
  approved_by: string;
  created_at: string;
  updated_at: string;
  action_history: CertificationActionHistory[];
  review_request?: CertificationDomainReviewRequest;
}

export interface CertificationActionHistory {
  action: string;
  old_value: string;
  new_value: string;
  username: string;
  timestamp: string;
  notes: string;
}

export interface CertificationProfileUpdate {
  profile_id: string;
  profile_type?: "business" | "personal";
  status?: "approved" | "pending" | "rejected" | "cancelled" | "review_requested";
  business?: CertificationProfileBusiness;
  personal?: CertificationProfilePersonal;
  verified_domains?: CertificationDomain[];
  admin_notes?: string;
  reason_for_adjustment?: string;
}

export interface CertificationDomain {
  domain: string;
  verification_hash: string;
  verification_status: string;
  generated_at: string;
  verified?: boolean;
  review_request?: CertificationDomainReviewRequest;
}

export interface CertificationProfileBusiness {
  business_name: string;
  tax_id: string;
  head_office: string;
  email: string;
  phone_number: string;
  business_type: string;
  documents: CertificationDocument[];
}

export interface CertificationProfilePersonal {
  full_name: string;
  contact_email: string;
  phone_number: string;
  country_region?: string;
  documents: CertificationDocument[];
}

export interface CertificationsParams {
  offset?: number;
  limit?: number;
  status?: string;
  search?: string;
  profileType?: string;
}

export interface CertificationsResponse {
  items: Certification[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface CertificationDocument {
  name?: string;
  presigned_url?: string;
  s3_key: string;
  size_bytes: number;
  uploaded_at: string;
}

export enum CertificationStatus {
  PENDING = "pending",
  APPROVED = "approved",
  REJECTED = "rejected",
  CANCELLED = "cancelled",
}

export enum CertificationSource {
  BUSINESS = "business",
  PERSONAL = "personal",
}

export interface CertificationsStats {
  by_profile_type: { business: number; personal: number };
  total_profiles: number;
}

export interface UploadBusinessOwnerEvidenceResponse {
  s3_key: string;
  original_filename: string;
  file_size: number;
  uploaded_at: string;
}

export type ApiLog = {
  action: string;
  old_value: string | null;
  new_value: string | null;
  username: string;
  timestamp: string;
  notes: string;
  summary?: string;
  key_analysis_factors?: string;
  finalized_score?: string;
  finalized_status?: string;
};

export type GroupedLogs = {
  timestamp: string;
  actions: {
    time: string;
    action_name: string;
    username: string;
    notes: string;
    old_value: string | null;
    new_value: string | null;
    _rawTimestamp: number;
    _timeKey: string;
    summary: string;
    key_analysis_factors: string;
    finalized_score: string;
    status: string;
  }[];
};

export interface CertificationDomainReviewRequest {
  explanation: string;
  documents: CertificationDocument[];
  requested_at: string;
  requested_by: string;
}

export interface UnRevokeProfileResponse {
  message: string;
  profile_id: string;
  total_domains: number;
  restored_domains: number;
  ignored_domains: number;
  restored_badges: number;
  restored_by: string;
  restored_at: string;
  reason: string;
}

export interface ImportUrlDataResponse {
  message: string;
  file_id: string;
  import_id: string;
  filename: string;
  file_size: number;
  column_count: number;
  columns: string[];
  s3_path: string;
  status: string;
  uploaded_at: string;
  uploaded_by: string;
  is_test: boolean;
  processing_result: {
    validation: {
      valid: true;
      errors: {
        header: [];
        rows: {};
      };
      summary: {
        total_rows: number;
        valid_rows: number;
        invalid_rows: number;
      };
    };
    processing: {
      inserted: number;
      updated: number;
      ignored: number;
      errors: number;
      error_details: {};
    };
    summary: {
      total_rows: number;
      valid_rows: number;
      invalid_rows: number;
      successfully_processed: number;
      failed_or_ignored: number;
    };
    detailed_records: ProcessingImportUrlDataResponseDetailRecord[];
    header: string[];
  };
}

export interface ProcessingImportUrlDataResponse {
  file_id: string;
  total_urls: number;
  total_processing_urls: number;
  succeeded_count: number;
  failed_count: number;
  ignored_count: number;
  status: string;
  is_overwrite: boolean;
  is_enhance_by_ai: boolean;
  uploaded_by: string;
  updated_at: string;
  created_at: string;
  processing_result: {
    validation: {
      summary: {
        total_rows: number;
        valid_rows: number;
        invalid_rows: number;
      };
      errors: { rows: {} };
    };
    processing: {
      inserted: number;
      updated: number;
      ignored: number;
      errors: number;
    };
    summary: {
      total_rows: number;
      valid_rows: number;
      invalid_rows: number;
      successfully_processed: number;
      failed_or_ignored: number;
    };
    detailed_records: ProcessingImportUrlDataResponseDetailRecord[];
    header: [];
  };
  detailed_records_presign_url: string;
}

export interface ProcessingImportUrlDataResponseDetailRecord {
  url: string;
  finalized_score: string;
  finalized_status: string;
  summary: string;
  key_analysis_factors: string;
  suspicious_type: string;
  description_note: string;
  evidence_images: string;
  reporter: string;
  error_reason: string;
}

export interface GeneratePresignedUploadUrlResponse {
  url: string;
  fields: {
    "Content-Type": string;
    "x-amz-meta-uploaded-by": string;
    "x-amz-meta-upload-timestamp": string;
    key: string;
    AWSAccessKeyId: string;
    "x-amz-security-token": string;
    policy: string;
    signature: string;
  };
  s3_key: string;
  file_id: string;
  expires_in: number;
}
